import React from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Spinner } from '../../../shared/components';
import {
  getSsoSettings,
  setSsoEnabled,
  testSsoSettings,
  updateSsoSettings,
  type SsoProtocol,
  type TenantSsoSettings,
} from '../services/ssoService';
import { listRoles } from '../services/roleService';
import type { Role } from '../types/Role';
import styles from './SecurityPage.module.css';

const CopyField: React.FC<{ label: string; value: string; copiedLabel: string; copyLabel: string }> = ({
  label,
  value,
  copiedLabel,
  copyLabel,
}) => {
  const [copied, setCopied] = React.useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // ignore
    }
  };

  return (
    <div className={styles.copyRow}>
      <span className={styles.copyLabel}>{label}</span>
      <input className={`text-input ${styles.copyValue}`} value={value} readOnly />
      <button type="button" className="btn btn-secondary" onClick={() => void onCopy()}>
        {copied ? copiedLabel : copyLabel}
      </button>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  hint?: string;
  required?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}> = ({ label, hint, required, wide, children }) => (
  <label className={wide ? styles.wide : undefined}>
    <span>
      {label}
      {required ? <span className="field-required-star" aria-hidden="true">*</span> : null}
    </span>
    {hint ? <span className={styles.fieldHint}>{hint}</span> : null}
    {children}
  </label>
);

export const SecurityPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [settings, setSettings] = React.useState<TenantSsoSettings | null>(null);
  const [protocol, setProtocol] = React.useState<SsoProtocol>('Oidc');
  const [allowedEmailDomains, setAllowedEmailDomains] = React.useState('');
  const [breakGlassEmail, setBreakGlassEmail] = React.useState('');
  const [jitEnabled, setJitEnabled] = React.useState(false);
  const [jitDefaultRoleId, setJitDefaultRoleId] = React.useState('');
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [oidcAuthority, setOidcAuthority] = React.useState('');
  const [oidcClientId, setOidcClientId] = React.useState('');
  const [oidcClientSecret, setOidcClientSecret] = React.useState('');
  const [oidcScopes, setOidcScopes] = React.useState('openid email profile');
  const [samlIdpEntityId, setSamlIdpEntityId] = React.useState('');
  const [samlIdpSsoUrl, setSamlIdpSsoUrl] = React.useState('');
  const [samlIdpSloUrl, setSamlIdpSloUrl] = React.useState('');
  const [samlIdpCertificate, setSamlIdpCertificate] = React.useState('');
  const [samlEmailAttributeName, setSamlEmailAttributeName] = React.useState('email');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const applySettings = React.useCallback((next: TenantSsoSettings) => {
    setSettings(next);
    setProtocol(next.protocol === 'Saml' ? 'Saml' : 'Oidc');
    setAllowedEmailDomains(next.allowedEmailDomains || '');
    setBreakGlassEmail(next.breakGlassEmail || '');
    setJitEnabled(next.jitEnabled === true);
    setJitDefaultRoleId(next.jitDefaultRoleId || '');
    setOidcAuthority(next.oidc.authority || '');
    setOidcClientId(next.oidc.clientId || '');
    setOidcScopes(next.oidc.scopes || 'openid email profile');
    setSamlIdpEntityId(next.saml.idpEntityId || '');
    setSamlIdpSsoUrl(next.saml.idpSsoUrl || '');
    setSamlIdpSloUrl(next.saml.idpSloUrl || '');
    setSamlEmailAttributeName(next.saml.emailAttributeName || 'email');
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [settingsResult, rolesResult] = await Promise.allSettled([
          getSsoSettings(),
          listRoles({ isActive: true, sortBy: 'name', sortDir: 'asc', page: 1, pageSize: 200 }),
        ]);
        if (!cancelled) {
          if (settingsResult.status === 'fulfilled') {
            applySettings(settingsResult.value);
          } else {
            throw settingsResult.reason;
          }

          setRoles(rolesResult.status === 'fulfilled' ? rolesResult.value : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('ssoLoadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [applySettings, t]);

  const locked = settings?.isEnabled === true;
  const configured = settings?.configured === true;
  const statusKey = locked ? 'on' : configured ? 'ready' : 'off';
  const copyLabel = t('copy', { ns: 'common', defaultValue: 'Copy' });
  const copiedLabel = t('ssoCopied');

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const next = await updateSsoSettings({
        protocol,
        allowedEmailDomains,
        breakGlassEmail,
        jitEnabled,
        jitDefaultRoleId: jitDefaultRoleId || null,
        oidcAuthority,
        oidcClientId,
        oidcClientSecret: oidcClientSecret || undefined,
        oidcScopes,
        samlIdpEntityId,
        samlIdpSsoUrl,
        samlIdpSloUrl,
        samlIdpCertificate: samlIdpCertificate || undefined,
        samlEmailAttributeName,
      });
      applySettings(next);
      setOidcClientSecret('');
      setSamlIdpCertificate('');
      setNotice(t('ssoSaved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ssoSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setError(null);
    setNotice(null);
    setTesting(true);
    try {
      const result = await testSsoSettings();
      setNotice(result.message || t('ssoTestPassed'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ssoTestFailed'));
    } finally {
      setTesting(false);
    }
  };

  const onToggleEnabled = async () => {
    if (!settings) {
      return;
    }

    const nextEnabled = !settings.isEnabled;
    const confirmMessage = nextEnabled ? t('ssoEnableConfirm') : t('ssoDisableConfirm');
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const next = await setSsoEnabled(nextEnabled);
      applySettings(next);
      setNotice(nextEnabled ? t('ssoEnabledNotice') : t('ssoDisabledNotice'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ssoSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="company-admin-page">
        <Spinner text={t('ssoLoading')} />
      </div>
    );
  }

  return (
    <div className={`company-admin-page ${styles.page}`}>
      <Breadcrumb items={[{ label: t('security'), path: '/admin/security' }]} />

      <div>
        <h2>{t('ssoTitle')}</h2>
        <p className={styles.intro}>{t('ssoHelp')}</p>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="success">{notice}</div> : null}

      <section className={`company-section ${styles.statusCard}`}>
        <div className={styles.statusCopy}>
          <span className={styles.statusLabel}>{t('ssoStatusLabel')}</span>
          <span className={`${styles.badge} ${statusKey === 'on' ? styles.badgeOn : statusKey === 'ready' ? styles.badgeReady : styles.badgeOff}`}>
            {t(`ssoStatus${statusKey === 'on' ? 'On' : statusKey === 'ready' ? 'Ready' : 'Off'}`)}
          </span>
          <p className={styles.statusText}>
            {t(`ssoStatus${statusKey === 'on' ? 'On' : statusKey === 'ready' ? 'Ready' : 'Off'}Help`)}
          </p>
        </div>
        <div className={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={() => void onTest()} disabled={testing || !configured}>
            {testing ? t('ssoTesting') : t('ssoTest')}
          </button>
          <button
            type="button"
            className={locked ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={() => void onToggleEnabled()}
            disabled={saving || !configured}
          >
            {locked ? t('ssoDisable') : t('ssoEnable')}
          </button>
        </div>
      </section>

      {locked ? <div className="company-tab-readonly-hint">{t('ssoLockedHint')}</div> : null}

      <form onSubmit={onSave}>
        <section className="company-section">
          <div className="company-section-head">
            <h3>{t('ssoStepProtocol')}</h3>
          </div>
          <p className={styles.hint}>{t('ssoStepProtocolHelp')}</p>
          <div className={styles.protocolGrid}>
            <button
              type="button"
              className={`${styles.protocolCard} ${protocol === 'Oidc' ? styles.protocolCardActive : ''}`}
              onClick={() => setProtocol('Oidc')}
              disabled={locked}
            >
              <span className={styles.protocolTitle}>{t('ssoProtocolOidc')}</span>
              <p className={styles.protocolHelp}>{t('ssoProtocolOidcHelp')}</p>
            </button>
            <button
              type="button"
              className={`${styles.protocolCard} ${protocol === 'Saml' ? styles.protocolCardActive : ''}`}
              onClick={() => setProtocol('Saml')}
              disabled={locked}
            >
              <span className={styles.protocolTitle}>{t('ssoProtocolSaml')}</span>
              <p className={styles.protocolHelp}>{t('ssoProtocolSamlHelp')}</p>
            </button>
          </div>
        </section>

        {settings ? (
          <section className="company-section" style={{ marginTop: 16 }}>
            <div className="company-section-head">
              <h3>{t('ssoStepCopy')}</h3>
            </div>
            <p className={styles.hint}>{t('ssoStepCopyHelp')}</p>
            <CopyField label={t('ssoEntityId')} value={settings.serviceProvider.entityId} copyLabel={copyLabel} copiedLabel={copiedLabel} />
            {protocol === 'Oidc' ? (
              <CopyField label={t('ssoOidcRedirect')} value={settings.serviceProvider.oidcRedirectUrl} copyLabel={copyLabel} copiedLabel={copiedLabel} />
            ) : (
              <CopyField label={t('ssoSamlAcs')} value={settings.serviceProvider.samlAcsUrl} copyLabel={copyLabel} copiedLabel={copiedLabel} />
            )}
          </section>
        ) : null}

        <section className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('ssoStepProvider')}</h3>
          </div>
          <p className={styles.hint}>{t('ssoStepProviderHelp')}</p>
          <div className="company-form-grid">
            {protocol === 'Oidc' ? (
              <>
                <Field label={t('ssoOidcAuthority')} hint={t('ssoOidcAuthorityHint')} required wide>
                  <input className="text-input" value={oidcAuthority} onChange={event => setOidcAuthority(event.target.value)} disabled={locked} placeholder="https://login.microsoftonline.com/{tenant}/v2.0" />
                </Field>
                <Field label={t('ssoOidcClientId')} hint={t('ssoOidcClientIdHint')} required>
                  <input className="text-input" value={oidcClientId} onChange={event => setOidcClientId(event.target.value)} disabled={locked} />
                </Field>
                <Field label={t('ssoOidcClientSecret')} hint={settings?.oidc.hasClientSecret ? t('ssoSecretKept') : t('ssoOidcClientSecretHint')}>
                  <input className="text-input" type="password" value={oidcClientSecret} onChange={event => setOidcClientSecret(event.target.value)} disabled={locked} autoComplete="new-password" />
                </Field>
                <Field label={t('ssoOidcScopes')} hint={t('ssoOidcScopesHint')} wide>
                  <input className="text-input" value={oidcScopes} onChange={event => setOidcScopes(event.target.value)} disabled={locked} />
                </Field>
              </>
            ) : (
              <>
                <Field label={t('ssoSamlEntityId')} hint={t('ssoSamlEntityIdHint')} required wide>
                  <input className="text-input" value={samlIdpEntityId} onChange={event => setSamlIdpEntityId(event.target.value)} disabled={locked} />
                </Field>
                <Field label={t('ssoSamlSsoUrl')} hint={t('ssoSamlSsoUrlHint')} required wide>
                  <input className="text-input" value={samlIdpSsoUrl} onChange={event => setSamlIdpSsoUrl(event.target.value)} disabled={locked} />
                </Field>
                <Field label={t('ssoSamlSloUrl')} hint={t('ssoSamlSloUrlHint')} wide>
                  <input className="text-input" value={samlIdpSloUrl} onChange={event => setSamlIdpSloUrl(event.target.value)} disabled={locked} />
                </Field>
                <Field label={t('ssoSamlCertificate')} hint={settings?.saml.hasCertificate ? t('ssoCertificateKept') : t('ssoSamlCertificateHint')} required wide>
                  <textarea className="text-input" value={samlIdpCertificate} onChange={event => setSamlIdpCertificate(event.target.value)} disabled={locked} rows={6} />
                </Field>
                <Field label={t('ssoSamlEmailAttribute')} hint={t('ssoSamlEmailAttributeHint')}>
                  <input className="text-input" value={samlEmailAttributeName} onChange={event => setSamlEmailAttributeName(event.target.value)} disabled={locked} />
                </Field>
              </>
            )}
          </div>
        </section>

        <section className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('ssoStepAccess')}</h3>
          </div>
          <p className={styles.hint}>{t('ssoStepAccessHelp')}</p>
          <label className={styles.jitRow}>
            <input
              type="checkbox"
              checked={jitEnabled}
              onChange={event => setJitEnabled(event.target.checked)}
              disabled={locked}
            />
            <span>
              <span className={styles.jitTitle}>{t('ssoJitEnabled')}</span>
              <span className={styles.fieldHint}>{t('ssoJitHelp')}</span>
            </span>
          </label>
          <div className="company-form-grid">
            <Field
              label={t('ssoAllowedDomains')}
              hint={jitEnabled ? t('ssoAllowedDomainsJitHint') : t('ssoAllowedDomainsHint')}
              required={jitEnabled}
              wide
            >
              <textarea className="text-input" value={allowedEmailDomains} onChange={event => setAllowedEmailDomains(event.target.value)} disabled={locked} rows={3} placeholder="company.com" />
            </Field>
            <Field label={t('ssoJitDefaultRole')} hint={t('ssoJitDefaultRoleHint')} required={jitEnabled} wide>
              <select
                className="text-input"
                value={jitDefaultRoleId}
                onChange={event => setJitDefaultRoleId(event.target.value)}
                disabled={locked}
              >
                <option value="">{t('ssoJitDefaultRolePlaceholder')}</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
                {jitDefaultRoleId && !roles.some(role => role.id === jitDefaultRoleId) ? (
                  <option value={jitDefaultRoleId}>{t('ssoJitDefaultRoleMissing')}</option>
                ) : null}
              </select>
            </Field>
            <Field label={t('ssoBreakGlassEmail')} hint={t('ssoBreakGlassHint')} wide>
              <input className="text-input" value={breakGlassEmail} onChange={event => setBreakGlassEmail(event.target.value)} disabled={locked} placeholder="admin@company.com" />
            </Field>
          </div>
        </section>

        <div className={styles.actions} style={{ marginTop: 16 }}>
          <button type="submit" className="btn btn-primary" disabled={saving || locked}>
            {saving ? t('saving', { ns: 'common', defaultValue: 'Saving…' }) : t('save', { ns: 'common' })}
          </button>
          {locked ? <p className={styles.actionsHint}>{t('ssoLockedHint')}</p> : <p className={styles.actionsHint}>{t('ssoSaveThenTest')}</p>}
        </div>
      </form>
    </div>
  );
};

export default SecurityPage;
